/**
 * 项目商品池页签 - 业务逻辑
 *
 * 项目下拉默认选第一个（PRD SCP-TAB-001 AC-03）；切换项目时页签恢复【已上架】、保留筛选与展示模式（AC-04、BR-12）；
 * 【重置】保留项目与页签（AC-12）；每次查询重新读取项目范围与字段配置，项目失权时自动切换（BR-10）；过期响应丢弃。
 */
import { useEffect, useReducer, useRef } from 'react'
import { message } from 'antd'
import type { FormInstance } from 'antd'
import { useLocation } from 'react-router-dom'
import { usePageCache, useSkipInitEffect } from '@/tools/utilHooks'
import { isResponseCodeSuccess } from '@/tools/util'
import type {
  OnSearchOption,
  SearchItemFilter,
} from '@/components/SearchFilter'
import {
  GET_PROJECT_POOL_PAGE,
  GET_PROJECT_POOL_PROJECTS,
  POST_PROJECT_POOL_EXPORT,
} from '@/api/projectPoolApi'
import reducer, {
  DEFAULT_STATUS_TAB,
  PoolFilter,
  PoolQuery,
  State,
  ViewMode,
  initialState,
  reloadStateAction,
  setDetailCodeAction,
  setExportingAction,
  setLoadingAction,
  setProjectsAction,
  setProjectsLoadingAction,
  setQueryAction,
  setResultAction,
  setSelectedIdsAction,
  setViewModeAction,
} from './reducer'

/** SearchFilter 查询条件 → 项目商品池接口筛选参数，空值不传 */
const toFilter = (values: SearchItemFilter): PoolFilter => ({
  xhCode: values.poolProductCode || undefined,
  customerTitle: values.poolCustomerTitle || undefined,
  brandName: values.poolBrandName || undefined,
  customerCategoryName: values.poolCategoryName || undefined,
  remark: values.poolRemark || undefined,
  standardCode: values.poolStandardCode || undefined,
  materialCode: values.poolMaterialCode || undefined,
})

/** 项目商品池接口筛选参数 → SearchFilter 查询条件（恢复缓存时回填输入框） */
const toFormValues = (filter: PoolFilter) => ({
  poolProductCode: filter.xhCode,
  poolCustomerTitle: filter.customerTitle,
  poolBrandName: filter.brandName,
  poolCategoryName: filter.customerCategoryName,
  poolRemark: filter.remark,
  poolStandardCode: filter.standardCode,
  poolMaterialCode: filter.materialCode,
})

/**
 * @param restore 是否从页面缓存恢复：从其他页面切回来时为真；新打开页面为假（忽略旧缓存）
 * @param active 【项目商品池】是否为当前页签：切走时清空勾选（用户确认）
 */
const useProjectPool = (restore: boolean, active: boolean) => {
  const requestSeq = useRef(0)
  const searchRef = useRef<FormInstance>(null)
  const { pathname } = useLocation()
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  // 与【我提报的】共用路由，单独一个缓存 key
  const cacheKey = `${pathname}#projectPool`
  // 打开详情前的焦点元素，关闭后归还焦点
  const detailTriggerRef = useRef<HTMLElement | null>(null)
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    projects,
    platformCode,
    filter,
    statusTab,
    pageNo,
    pageSize,
    selectedIds,
  } = state
  const currentProject = projects.find(
    (project) => project.code === platformCode,
  )
  // 当前项目的可见字段，决定表格列与图标
  const fields = currentProject?.fields || []
  // 当前项目是否允许导出，决定【物料导出】按钮是否展示
  const exportEnabled = currentProject?.exportEnabled || false

  /**
   * 查询列表，同时重新读取项目范围与字段配置。
   *
   * @param reloadConfig 进入页签时项目列表刚读取过，传 false 避免重复请求
   */
  const fetchList = async (query: PoolQuery, reloadConfig = true) => {
    dispatch(setQueryAction(query))
    const seq = ++requestSeq.current
    if (!query.platformCode) {
      dispatch(setResultAction({ list: [], total: 0 }))
      dispatch(setLoadingAction(false))
      return
    }
    dispatch(setLoadingAction(true))
    try {
      const [projectsRes, res] = await Promise.all([
        reloadConfig ? GET_PROJECT_POOL_PROJECTS() : undefined,
        GET_PROJECT_POOL_PAGE({
          projectCode: query.platformCode,
          pageNo: query.pageNo,
          pageSize: query.pageSize,
          statusTab: query.statusTab,
          ...query.filter,
        }),
      ])
      // 期间已发起新查询（如快速切换项目），丢弃本次结果
      if (seq !== requestSeq.current) return
      if (projectsRes && isResponseCodeSuccess(projectsRes.code)) {
        dispatch(setProjectsAction(projectsRes.data))
        // 当前项目已无权限（关系取消、授权关闭等）：清空旧数据，切到第一个仍有权限的项目
        if (
          !projectsRes.data.some(
            (project) => project.code === query.platformCode,
          )
        ) {
          const next = projectsRes.data[0]?.code
          message.warning(
            next
              ? '当前项目已无查看权限，已切换至其他项目'
              : '当前项目已无查看权限',
          )
          fetchList(
            {
              ...query,
              platformCode: next,
              statusTab: DEFAULT_STATUS_TAB,
              pageNo: 1,
            },
            false,
          )
          return
        }
      }
      dispatch(
        setResultAction(
          isResponseCodeSuccess(res.code)
            ? { list: res.data.content, total: res.data.totalCount }
            : { list: [], total: 0 },
        ),
      )
    } finally {
      if (seq === requestSeq.current) dispatch(setLoadingAction(false))
    }
  }

  const loadProjects = async () => {
    dispatch(setProjectsLoadingAction(true))
    try {
      const res = await GET_PROJECT_POOL_PROJECTS()
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setProjectsAction(res.data))
        fetchList(
          {
            platformCode: res.data[0]?.code,
            filter: {},
            statusTab: DEFAULT_STATUS_TAB,
            pageNo: 1,
            pageSize,
          },
          false,
        )
      }
    } finally {
      dispatch(setProjectsLoadingAction(false))
    }
  }

  /**
   * 恢复离开前的项目、页签、筛选、分页、展示模式与列表，并回填查询输入框；勾选清空（用户确认），
   * 详情抽屉与导出转圈不恢复。
   * 离开时请求尚未返回的，按离开前的条件重新查询。
   */
  const restorePageCache = (cache: State) => {
    dispatch(
      reloadStateAction({
        ...cache,
        selectedIds: [],
        detailCode: undefined,
        exporting: false,
      }),
    )
    searchRef.current?.setFieldsValue(toFormValues(cache.filter))
    if (cache.projectsLoading) {
      loadProjects()
    } else if (cache.loading) {
      fetchList(cache, false)
    }
  }

  const initPage = () => {
    const cache: State | null = restore ? getCacheWithKey(cacheKey) : null
    if (cache) {
      restorePageCache(cache)
    } else {
      loadProjects()
    }
  }

  const savePageCache = () => setCacheWithKey(state, cacheKey)

  useEffect(() => {
    initPage()
  }, [])

  useSkipInitEffect(() => {
    savePageCache()
  }, [state])

  // SearchFilter 查询 / 重置（重置时 values 为空、isReset 为真）；项目列表未加载完成前忽略，进入页签的首次查询由 loadProjects 发起
  const search = (values: SearchItemFilter, options?: OnSearchOption) => {
    if (!platformCode) return
    fetchList({
      platformCode,
      filter: options?.isReset ? {} : toFilter(values),
      statusTab,
      pageNo: 1,
      pageSize,
    })
  }

  const changeProject = (code: string) =>
    fetchList({
      platformCode: code,
      filter,
      statusTab: DEFAULT_STATUS_TAB,
      pageNo: 1,
      pageSize,
    })

  const changeTab = (tab: string) =>
    fetchList({ platformCode, filter, statusTab: tab, pageNo: 1, pageSize })

  // 切换每页条数时分页回调 page=1
  const changePage = (page: number, size?: number) =>
    fetchList({
      platformCode,
      filter,
      statusTab,
      pageNo: page,
      pageSize: size || pageSize,
    })

  const refresh = () =>
    fetchList({ platformCode, filter, statusTab, pageNo, pageSize })

  const changeViewMode = (mode: ViewMode) => dispatch(setViewModeAction(mode))

  const setSelectedIds = (ids: string[]) => dispatch(setSelectedIdsAction(ids))

  // 切到【我提报的】时清空勾选，切回来时表格重新挂载即为未勾选；其他状态保持（与 MPM 一致，用户确认）
  const clearSelectionOnLeave = () => {
    if (!active) setSelectedIds([])
  }

  useSkipInitEffect(() => {
    clearSelectionOnLeave()
  }, [active])

  const toggleSelected = (id: string, checked: boolean) =>
    setSelectedIds(
      checked
        ? [...selectedIds, id]
        : selectedIds.filter((item) => item !== id),
    )

  /**
   * 物料导出（进 SCP 任务中心）：有勾选只导出勾选，否则导出当前项目、页签与筛选条件下全部数据；
   * 失败由请求拦截器提示后端文案，勾选保留。
   */
  const exportPool = async () => {
    if (!platformCode) return
    dispatch(setExportingAction(true))
    try {
      const res = await POST_PROJECT_POOL_EXPORT({
        projectCode: platformCode,
        statusTab,
        ...filter,
        selectedIds,
      })
      if (isResponseCodeSuccess(res.code)) message.success(res.msg)
    } finally {
      dispatch(setExportingAction(false))
    }
  }

  const openDetail = (xhCode: string) => {
    detailTriggerRef.current = document.activeElement as HTMLElement | null
    dispatch(setDetailCodeAction(xhCode))
  }

  const closeDetail = () => dispatch(setDetailCodeAction(undefined))

  const restoreFocus = () => detailTriggerRef.current?.focus()

  return {
    state,
    searchRef,
    fields,
    exportEnabled,
    search,
    changeProject,
    changeTab,
    changePage,
    refresh,
    changeViewMode,
    setSelectedIds,
    toggleSelected,
    exportPool,
    openDetail,
    closeDetail,
    restoreFocus,
  }
}

export default useProjectPool
/**
 * 项目商品池页签 - 业务逻辑
 *
 * 项目下拉默认选第一个（PRD SCP-TAB-001 AC-03）；切换项目时页签恢复【已上架】、保留筛选与展示模式（AC-04、BR-12）；
 * 【重置】保留项目与页签（AC-12）；每次查询重新读取项目范围与字段配置，项目失权时自动切换（BR-10）；过期响应丢弃。
 */
import { useEffect, useReducer, useRef } from 'react'
import { message } from 'antd'
import type { FormInstance } from 'antd'
import { useLocation } from 'react-router-dom'
import { usePageCache, useSkipInitEffect } from '@/tools/utilHooks'
import { isResponseCodeSuccess } from '@/tools/util'
import type {
  OnSearchOption,
  SearchItemFilter,
} from '@/components/SearchFilter'
import {
  GET_PROJECT_POOL_PAGE,
  GET_PROJECT_POOL_PROJECTS,
  POST_PROJECT_POOL_EXPORT,
} from '@/api/projectPoolApi'
import reducer, {
  DEFAULT_STATUS_TAB,
  PoolFilter,
  PoolQuery,
  State,
  ViewMode,
  initialState,
  reloadStateAction,
  setDetailCodeAction,
  setExportingAction,
  setLoadingAction,
  setProjectsAction,
  setProjectsLoadingAction,
  setQueryAction,
  setResultAction,
  setSelectedIdsAction,
  setViewModeAction,
} from './reducer'

/** SearchFilter 查询条件 → 项目商品池接口筛选参数，空值不传 */
const toFilter = (values: SearchItemFilter): PoolFilter => ({
  xhCode: values.poolProductCode || undefined,
  customerTitle: values.poolCustomerTitle || undefined,
  brandName: values.poolBrandName || undefined,
  customerCategoryName: values.poolCategoryName || undefined,
  remark: values.poolRemark || undefined,
  standardCode: values.poolStandardCode || undefined,
  materialCode: values.poolMaterialCode || undefined,
})

/** 项目商品池接口筛选参数 → SearchFilter 查询条件（恢复缓存时回填输入框） */
const toFormValues = (filter: PoolFilter) => ({
  poolProductCode: filter.xhCode,
  poolCustomerTitle: filter.customerTitle,
  poolBrandName: filter.brandName,
  poolCategoryName: filter.customerCategoryName,
  poolRemark: filter.remark,
  poolStandardCode: filter.standardCode,
  poolMaterialCode: filter.materialCode,
})

/**
 * @param restore 是否从页面缓存恢复：从其他页面切回来时为真；新打开页面为假（忽略旧缓存）
 * @param active 【项目商品池】是否为当前页签：切走时清空勾选（用户确认）
 */
const useProjectPool = (restore: boolean, active: boolean) => {
  const requestSeq = useRef(0)
  const searchRef = useRef<FormInstance>(null)
  const { pathname } = useLocation()
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  // 与【我提报的】共用路由，单独一个缓存 key
  const cacheKey = `${pathname}#projectPool`
  // 打开详情前的焦点元素，关闭后归还焦点
  const detailTriggerRef = useRef<HTMLElement | null>(null)
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    projects,
    platformCode,
    filter,
    statusTab,
    pageNo,
    pageSize,
    selectedIds,
  } = state
  const currentProject = projects.find(
    (project) => project.code === platformCode,
  )
  // 当前项目的可见字段，决定表格列与图标
  const fields = currentProject?.fields || []
  // 当前项目是否允许导出，决定【物料导出】按钮是否展示
  const exportEnabled = currentProject?.exportEnabled || false

  /**
   * 查询列表，同时重新读取项目范围与字段配置。
   *
   * @param reloadConfig 进入页签时项目列表刚读取过，传 false 避免重复请求
   */
  const fetchList = async (query: PoolQuery, reloadConfig = true) => {
    dispatch(setQueryAction(query))
    const seq = ++requestSeq.current
    if (!query.platformCode) {
      dispatch(setResultAction({ list: [], total: 0 }))
      dispatch(setLoadingAction(false))
      return
    }
    dispatch(setLoadingAction(true))
    try {
      const [projectsRes, res] = await Promise.all([
        reloadConfig ? GET_PROJECT_POOL_PROJECTS() : undefined,
        GET_PROJECT_POOL_PAGE({
          projectCode: query.platformCode,
          pageNo: query.pageNo,
          pageSize: query.pageSize,
          statusTab: query.statusTab,
          ...query.filter,
        }),
      ])
      // 期间已发起新查询（如快速切换项目），丢弃本次结果
      if (seq !== requestSeq.current) return
      if (projectsRes && isResponseCodeSuccess(projectsRes.code)) {
        dispatch(setProjectsAction(projectsRes.data))
        // 当前项目已无权限（关系取消、授权关闭等）：清空旧数据，切到第一个仍有权限的项目
        if (
          !projectsRes.data.some(
            (project) => project.code === query.platformCode,
          )
        ) {
          const next = projectsRes.data[0]?.code
          message.warning(
            next
              ? '当前项目已无查看权限，已切换至其他项目'
              : '当前项目已无查看权限',
          )
          fetchList(
            {
              ...query,
              platformCode: next,
              statusTab: DEFAULT_STATUS_TAB,
              pageNo: 1,
            },
            false,
          )
          return
        }
      }
      dispatch(
        setResultAction(
          isResponseCodeSuccess(res.code)
            ? { list: res.data.content, total: res.data.totalCount }
            : { list: [], total: 0 },
        ),
      )
    } finally {
      if (seq === requestSeq.current) dispatch(setLoadingAction(false))
    }
  }

  const loadProjects = async () => {
    dispatch(setProjectsLoadingAction(true))
    try {
      const res = await GET_PROJECT_POOL_PROJECTS()
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setProjectsAction(res.data))
        fetchList(
          {
            platformCode: res.data[0]?.code,
            filter: {},
            statusTab: DEFAULT_STATUS_TAB,
            pageNo: 1,
            pageSize,
          },
          false,
        )
      }
    } finally {
      dispatch(setProjectsLoadingAction(false))
    }
  }

  /**
   * 恢复离开前的项目、页签、筛选、分页、展示模式与列表，并回填查询输入框；勾选清空（用户确认），
   * 详情抽屉与导出转圈不恢复。
   * 离开时请求尚未返回的，按离开前的条件重新查询。
   */
  const restorePageCache = (cache: State) => {
    dispatch(
      reloadStateAction({
        ...cache,
        selectedIds: [],
        detailCode: undefined,
        exporting: false,
      }),
    )
    searchRef.current?.setFieldsValue(toFormValues(cache.filter))
    if (cache.projectsLoading) {
      loadProjects()
    } else if (cache.loading) {
      fetchList(cache, false)
    }
  }

  const initPage = () => {
    const cache: State | null = restore ? getCacheWithKey(cacheKey) : null
    if (cache) {
      restorePageCache(cache)
    } else {
      loadProjects()
    }
  }

  const savePageCache = () => setCacheWithKey(state, cacheKey)

  useEffect(() => {
    initPage()
  }, [])

  useSkipInitEffect(() => {
    savePageCache()
  }, [state])

  // SearchFilter 查询 / 重置（重置时 values 为空、isReset 为真）；项目列表未加载完成前忽略，进入页签的首次查询由 loadProjects 发起
  const search = (values: SearchItemFilter, options?: OnSearchOption) => {
    if (!platformCode) return
    fetchList({
      platformCode,
      filter: options?.isReset ? {} : toFilter(values),
      statusTab,
      pageNo: 1,
      pageSize,
    })
  }

  const changeProject = (code: string) =>
    fetchList({
      platformCode: code,
      filter,
      statusTab: DEFAULT_STATUS_TAB,
      pageNo: 1,
      pageSize,
    })

  const changeTab = (tab: string) =>
    fetchList({ platformCode, filter, statusTab: tab, pageNo: 1, pageSize })

  // 切换每页条数时分页回调 page=1
  const changePage = (page: number, size?: number) =>
    fetchList({
      platformCode,
      filter,
      statusTab,
      pageNo: page,
      pageSize: size || pageSize,
    })

  const refresh = () =>
    fetchList({ platformCode, filter, statusTab, pageNo, pageSize })

  const changeViewMode = (mode: ViewMode) => dispatch(setViewModeAction(mode))

  const setSelectedIds = (ids: string[]) => dispatch(setSelectedIdsAction(ids))

  // 切到【我提报的】时清空勾选，切回来时表格重新挂载即为未勾选；其他状态保持（与 MPM 一致，用户确认）
  const clearSelectionOnLeave = () => {
    if (!active) setSelectedIds([])
  }

  useSkipInitEffect(() => {
    clearSelectionOnLeave()
  }, [active])

  const toggleSelected = (id: string, checked: boolean) =>
    setSelectedIds(
      checked
        ? [...selectedIds, id]
        : selectedIds.filter((item) => item !== id),
    )

  /**
   * 物料导出（进 SCP 任务中心）：有勾选只导出勾选，否则导出当前项目、页签与筛选条件下全部数据；
   * 失败由请求拦截器提示后端文案，勾选保留。
   */
  const exportPool = async () => {
    if (!platformCode) return
    dispatch(setExportingAction(true))
    try {
      const res = await POST_PROJECT_POOL_EXPORT({
        projectCode: platformCode,
        statusTab,
        ...filter,
        selectedIds,
      })
      if (isResponseCodeSuccess(res.code)) message.success(res.msg)
    } finally {
      dispatch(setExportingAction(false))
    }
  }

  const openDetail = (xhCode: string) => {
    detailTriggerRef.current = document.activeElement as HTMLElement | null
    dispatch(setDetailCodeAction(xhCode))
  }

  const closeDetail = () => dispatch(setDetailCodeAction(undefined))

  const restoreFocus = () => detailTriggerRef.current?.focus()

  return {
    state,
    searchRef,
    fields,
    exportEnabled,
    search,
    changeProject,
    changeTab,
    changePage,
    refresh,
    changeViewMode,
    setSelectedIds,
    toggleSelected,
    exportPool,
    openDetail,
    closeDetail,
    restoreFocus,
  }
}

export default useProjectPool
